/**
 * Plan → cart line, shared by /pricing (PricingContainer) and the storefront
 * (/shop, /shop/plans) through the `addPlanToCart` thunk. Pure: no dispatch,
 * no tracking.
 *
 * "Cart plan" shape (what PricingContainer has always used):
 *   { id: slug, name, priceValue, planTypeId?, subscription?: { available, pricePerMonthValue } }
 *
 * Pricing rules (mirrored and enforced server-side in order.service.ts
 * validateOrderItems — the client price is only a quote):
 *   - one plan per cart (the thunk removes any plan already in it);
 *   - monthly = the plan's per-cycle amount;
 *   - upgrade (logged-in user on a lower paid plan): same payment type pays
 *     target − amount paid (`plan.paidAmount`, else the current plan's price);
 *     cross-type (monthly ↔ one-time) pays the full target price.
 */
import { PLAN_TYPE_IDS } from "../../common/constants";

const AMOUNT_PAISE_TO_RUPEES = 100;

/** Plan slug → PLAN_TYPE_IDS (the `planId` the server and profile use). */
export const PLAN_SLUG_TO_TYPE = {
  pro: PLAN_TYPE_IDS.PRO,
  premium: PLAN_TYPE_IDS.PREMIUM,
  ultimate: PLAN_TYPE_IDS.ULTIMATE,
};

export const planTypeIdOf = (plan) =>
  plan?.planTypeId ?? PLAN_SLUG_TO_TYPE[plan?.id] ?? plan?.planId ?? PLAN_TYPE_IDS.FREE;

/** API pricing-plan document → cart plan (monthly only with a Razorpay plan). */
export function toCartPlan(apiPlan) {
  const sub = apiPlan?.subscription;
  return {
    id: apiPlan.slug,
    name: apiPlan.displayName || apiPlan.planName || apiPlan.slug,
    priceValue: apiPlan.priceInr,
    planTypeId: PLAN_SLUG_TO_TYPE[apiPlan.slug] ?? apiPlan.planId,
    subscription: sub?.razorpayPlanId
      ? {
          available: true,
          pricePerMonth: sub.displayPrice?.replace("/month", "") || "",
          pricePerMonthValue: sub.amountPerCycle ? sub.amountPerCycle / AMOUNT_PAISE_TO_RUPEES : 0,
        }
      : undefined,
  };
}

/**
 * What a visitor can do with a plan given their current one:
 * "buy" | "upgrade" | "current" | "lower" (current/lower cannot be bought).
 */
export function planPurchaseState(plan, userPlan) {
  const current = userPlan?.id || PLAN_TYPE_IDS.FREE;
  const target = planTypeIdOf(plan);
  if (current <= PLAN_TYPE_IDS.FREE) return "buy";
  if (target === current) return "current";
  if (target < current) return "lower";
  return "upgrade";
}

/**
 * The cart line for `plan` (exactly PricingContainer's former inline logic).
 * `plans` = all cart plans (to find the user's current plan price);
 * `userPlan` = state.user.gamePlayerProfile.plan ({ id, paidAmount, paymentType }).
 */
export function buildPlanCartItem({ plan, isSubscription = false, userPlan, plans = [] }) {
  const currentPlanTypeId = userPlan?.id || PLAN_TYPE_IDS.FREE;
  const targetPlanTypeId = planTypeIdOf(plan);
  const isUpgrade = currentPlanTypeId > PLAN_TYPE_IDS.FREE && targetPlanTypeId > currentPlanTypeId;

  let finalPrice = plan.priceValue;
  let description = `${plan.name} Plan - Comprehensive study abroad support`;

  // Differentials only apply for same-type upgrades; cross-type charges full price.
  const currentPaymentType = userPlan?.paymentType;
  const isCrossTypeUpgrade =
    isUpgrade &&
    currentPaymentType &&
    ((currentPaymentType === "subscription" && !isSubscription) ||
      (currentPaymentType !== "subscription" && isSubscription));

  if (isUpgrade && !isCrossTypeUpgrade) {
    const currentPlanEntry = plans.find((p) => planTypeIdOf(p) === currentPlanTypeId);
    const currentPlanName = currentPlanEntry?.name || "Current";
    // Actual amount paid (profile) beats the current list price, so users who
    // paid old prices or are on a subscription get the right differential.
    const currentPrice = userPlan?.paidAmount ?? currentPlanEntry?.priceValue ?? 0;
    finalPrice = plan.priceValue - currentPrice;
    description = `Upgrade from ${currentPlanName} to ${plan.name} Plan`;
  } else if (isUpgrade && isCrossTypeUpgrade) {
    description = `Upgrade to ${plan.name} Plan`;
  }

  if (isSubscription) {
    finalPrice = plan.subscription.pricePerMonthValue;
    description = `${plan.name} Plan - ₹${plan.subscription.pricePerMonthValue}/month`;
  }

  const item = {
    _id: plan.id,
    name: plan.name,
    description,
    priceInr: isSubscription ? plan.subscription.pricePerMonthValue : finalPrice,
    category: "Study Abroad Plans",
    status: "live",
    isPlan: true,
    isUpgrade,
    isSubscription,
    ...(isSubscription && { subscriptionPricePerMonth: plan.subscription.pricePerMonthValue }),
    ...(isUpgrade && {
      fromPlanId: currentPlanTypeId,
      toPlanId: targetPlanTypeId,
      originalPrice: plan.priceValue,
    }),
  };
  return { item, isUpgrade, currentPlanTypeId, targetPlanTypeId };
}
